import React, { createContext, useContext, useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import * as yup from "yup";

import useActionAutomaticSequence from "../hooks/useActionAutomaticSequence";
import { AutomaticSequencing } from "../models/automatic.sequencing";
import { Order } from "../models/order";
import { Staff } from "../models/staff";
import { StorageBox } from "../models/storage.box";
import { StorageBoxType } from "../models/storage.box.type";
import { Transshipment, TransshipmentType } from "../models/transshipment";
import { User } from "../models/user";
import StaffService from "../services/staff.service";
import StorageBoxService from "../services/storage.box.service";
import StorageBoxTypeService from "../services/storage.box.type.service";
import TransshipmentService from "../services/transshipment.service";
import TransshipmentTypeService from "../services/transshipment.type.service";
import UserService from "../services/user.service";
import {
  AutomaticSequenceOptions,
  Status,
  TaskStatus,
  TransshipmentSearchType,
  TransshipmentWhatToDo,
  UserTypes,
  TransshipmentWhereToLoad,
} from "../util/enums";
import { TransshipmentKeys } from "../util/queries";
import { useAppState } from "./StateContext";

interface TransshipmentContextProps {
  statuses?: string[];
  staffs?: Staff[];
  whatsToDo?: string[];
  whereToLoad?: string[];
  searchTypes?: string[];
  leaders?: User[];
  isLeadersListLoading: boolean;
  storageBoxes?: StorageBox[];
  storageBoxesFleets?: StorageBox[];
  storageBoxTypes?: StorageBoxType[];
  transshipment?: Transshipment;
  setTransshipment: (transshipment: Transshipment) => void;
  transshipmentTypes?: TransshipmentType[];
  sequence?: AutomaticSequencing;
  errors: yup.ValidationError[];
  setErrors: (errors: yup.ValidationError[]) => void;
  isLoadingEditTransshipment?: boolean;
  orders: Order[];
  setOrders: (orders: Order[]) => void;
  external?: boolean;
  setExternal: (external?: boolean) => void;
  type?: string;
  setType: (type: string) => void;
  reference?: string;
  setReference: (reference: string) => void;
  storageBox?: StorageBox;
  setStorageBox: (storageBox?: StorageBox) => void;
  isDisabledToEdit?: boolean;
  setCountOrders: (count: number) => void;
  countOrders: number;
}

export interface TransshipmentParams {
  transshipmentId?: string;
}

const TransshipmentContext = createContext<TransshipmentContextProps>(
  {} as TransshipmentContextProps
);

interface TransshipmentProviderProps {
  children: React.ReactChild;
}

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const CACHE_TIME = 5 * 60 * 60 * 1000; // 5 horas

const TransshipmentProvider = ({ children }: TransshipmentProviderProps) => {
  const { transshipmentId } = useParams<TransshipmentParams>();

  const { currentUser, warehouse } = useAppState();

  const [transshipment, setTransshipment] = useState<Transshipment>({
    status: TaskStatus.CREATED,
    createdBy: currentUser,
    createdById: currentUser?.id,
  });
  const [orders, setOrders] = useState<Order[]>([]);
  const [countOrders, setCountOrders] = useState<number>(0);

  const [sequence, setSequence] = useState<AutomaticSequencing>();
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const [external, setExternal] = useState<boolean | undefined>(false);
  const [type, setType] = useState(TransshipmentSearchType.ORDER.toString());
  const [reference, setReference] = useState("");
  const [storageBox, setStorageBox] = useState<StorageBox>();

  const [statusStoragedTransshipment, setStatusStoragedTransshipment] = useState<string>();

  const { getAutomaticSequenceByType } = useActionAutomaticSequence();

  getAutomaticSequenceByType(AutomaticSequenceOptions.TRANSSHIPMENT, !transshipmentId, setSequence);

  const { data: transshipmentToEdit, isFetching: isLoadingEditTransshipment } = useQuery(
    ["transshipment.getById"],
    async () => {
      const response = await TransshipmentService.getEditTransshipment(Number(transshipmentId));

      return response?.data;
    },
    {
      enabled: !!transshipmentId,
    }
  );

  const { data: transshipmentTypes } = useQuery(
    ["transshipment.type.getAll"],
    async () => {
      const response = await TransshipmentTypeService.getAll();

      return Array.isArray(response?.data) ? response?.data : [];
    },
    { enabled: true }
  );

  const { data: staffs } = useQuery(
    ["staff.getAll"],
    async () => {
      const response = await StaffService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([
          ["firstName", "asc"],
          ["lastName", "asc"],
        ]),
      });

      return Array.isArray(response?.data) ? response?.data : [];
    },
    {
      enabled: !!currentUser?.companyId,
    }
  );

  const { data: storageBoxes } = useQuery({
    queryKey: ["storage.boxes.and.units.list", warehouse, external],
    queryFn: async () => {
      const response = await StorageBoxService.getAll({
        companyId: currentUser?.companyId,
        isActive: 1,
        isFleet: 0,
        singleUse: 0,
        sortBy: JSON.stringify([["number", "asc"]]),
        include: !external
          ? JSON.stringify([
              {
                association: "warehouses",
                where: warehouse ? { id: warehouse.id } : null,
              },
              {
                association: "storageBoxType",
                attributes: ["id", "name"],
              },
            ])
          : undefined,
      });

      if (response && response?.data && Array.isArray(response?.data)) return response.data;

      return [] as StorageBox[];
    },
    enabled: !!(currentUser?.companyId && warehouse?.id),
  });

  const { data: storageBoxTypes } = useQuery({
    queryKey: ["storage.boxes.types.list", currentUser?.companyId],
    queryFn: async () => {
      const response = await StorageBoxTypeService.getAll({
        companyId: currentUser?.companyId,
        include: JSON.stringify({
          association: "storageBoxes",
          attributes: ["id"],
          limit: 1,
          where: { isActive: true, singleUse: false },
        }),
        isActive: "1",
        sortBy: JSON.stringify([["name"]]),
      });

      if (response && response?.data && Array.isArray(response?.data))
        return response.data.filter((type) => type.storageBoxes?.length === 0);

      return [] as StorageBoxType[];
    },
    enabled: !!currentUser?.companyId,
  });

  const { data: storageBoxesFleets } = useQuery({
    queryKey: ["storage.boxes.fleets.list", warehouse],
    queryFn: async () => {
      const response = await StorageBoxService.getAll({
        companyId: currentUser?.companyId,
        isActive: 1,
        isFleet: 1,
        singleUse: 0,
        sortBy: JSON.stringify([["number", "asc"]]),
        include: JSON.stringify([
          {
            association: "fleet",
            required: true,
            attributes: ["id"],
            where: {
              status: Status.ACTIVE,
              valid: 1,
              isLoadable: 1,
              warehouseId: warehouse?.id,
            },
          },
        ]),
      });

      if (response && response?.data && Array.isArray(response?.data)) return response.data;

      return [] as StorageBox[];
    },
    enabled: !!(currentUser?.companyId && warehouse?.id),
  });

  const getTransshipmentsAssigneeLeader = () => {
    const includeUserCompanies = {
      association: "usersCompanies",
      required: true,
      where: {
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        leader: 1,
        typeUser: [UserTypes.ADMIN, UserTypes.MOVING_WAREHOUSE, UserTypes.OPERATIONS],
      },
    };

    return useQuery({
      queryKey: TransshipmentKeys.getAll([currentUser?.companyId, includeUserCompanies]),
      queryFn: async () => {
        const response = await UserService.getAll({
          include: JSON.stringify([includeUserCompanies]),
          sortBy: JSON.stringify([["name", "asc"]]),
        });

        if (response && response?.data?.length > 0) return response.data;

        return [];
      },
      cacheTime: CACHE_TIME,
      staleTime: STALE_TIME,
      enabled: !!currentUser?.companyId,
    });
  };

  const { data: leaders, isFetching: isLeadersListLoading } = getTransshipmentsAssigneeLeader();

  useEffect(() => {
    if (transshipmentToEdit) {
      setTransshipment(transshipmentToEdit);
      setStatusStoragedTransshipment(transshipmentToEdit?.status);
    }
  }, [transshipmentToEdit]);

  useEffect(() => {
    if (sequence && !transshipmentId && !transshipment?.reference) {
      setTransshipment((prev) => {
        return {
          ...prev,
          reference: `${sequence?.prefix ? sequence.prefix : ""}${
            sequence?.nextValue ? sequence.nextValue : ""
          }`,
        };
      });
    }
  }, [sequence]);

  const [statuses] = useState([
    TaskStatus.CREATED,
    TaskStatus.DRAFT,
    TaskStatus.PUBLISHED,
    TaskStatus.STARTED,
    TaskStatus.IN_PROGRESS,
    TaskStatus.COMPLETED,
    TaskStatus.CANCELLED,
  ]);

  const [searchTypes] = useState([
    TransshipmentSearchType.FLEET,
    TransshipmentSearchType.ORDER,
    TransshipmentSearchType.STORAGE_BOX,
  ]);

  const [whatsToDo] = useState([TransshipmentWhatToDo.LOAD, TransshipmentWhatToDo.UNLOAD]);
  const [whereToLoad] = useState([
    TransshipmentWhereToLoad.FLEET,
    TransshipmentWhereToLoad.STORAGE_BOX,
    TransshipmentWhereToLoad.STORAGE_BOX_SINGLE_USE,
  ]);

  return (
    <TransshipmentContext.Provider
      value={{
        countOrders,
        setCountOrders,
        errors,
        setErrors,
        statuses,
        staffs,
        searchTypes,
        whatsToDo,
        whereToLoad,
        leaders,
        isLeadersListLoading,
        storageBoxes,
        storageBoxesFleets,
        storageBoxTypes,
        transshipment,
        setTransshipment,
        sequence,
        transshipmentTypes,
        isLoadingEditTransshipment,
        orders,
        setOrders,
        external,
        setExternal,
        type,
        setType,
        reference,
        setReference,
        storageBox,
        setStorageBox,
        isDisabledToEdit: !!(
          statusStoragedTransshipment &&
          (statusStoragedTransshipment === TaskStatus.STARTED ||
            statusStoragedTransshipment === TaskStatus.IN_PROGRESS)
        ),
      }}
    >
      {children}
    </TransshipmentContext.Provider>
  );
};

function useTransshipmentState() {
  const context = useContext(TransshipmentContext);

  if (!context) {
    throw new Error("useTransshipmentState must be used within the TransshipmentProvider");
  }

  return context;
}

export { useTransshipmentState, TransshipmentContext, TransshipmentProvider };
